import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, Menu, Popover, useOverlayContext } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { ArrowRight } from 'seagull/icons/arrows';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { getTestIds } from 'seagull/utils/TestIds';
import { CustomPageTemplatesModal } from '~/components/CustomPageTemplates/CustomPageTemplatesModal/CustomPageTemplatesModal';
import { useShowSelectCustomPageTypeModal } from '~/components/SelectCustomPageTypeModal/SelectCustomPageTypeModal';
import { useCloneCustomPageText } from '~/hooks/reactQuery/mutations/useCloneCustomPageText';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useFilePicker } from '~/hooks/useFilePicker';
import { useUploadCustomPagePdf } from '~/hooks/useUploadCustomPagePdf';
import { useLocaleKeys } from '~/i18n';
import { createBlankVideoPage, createBlankTextPage, createBlankPdfPage } from '~/models/custom-page/model';
import sectionStyles from '../CustomPagesSection.module.scss';
export const GetAddCustomPageDropdownTID = (parentTestId)=>getTestIds(parentTestId);
export const AddCustomPageDropdown = (param)=>{
    let { testId, nextDisplayOrder, onAddPage } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetAddCustomPageDropdownTID(testId), [
        testId
    ]);
    const { templates, isLoading } = useCustomPageTemplates({
        refreshOnHookMount: true
    });
    const { showModal, showErrorToast } = useOverlayContext();
    const showSelectTypeModal = useShowSelectCustomPageTypeModal();
    const { cloneText } = useCloneCustomPageText();
    const { handleFileSelected: handlePdfFileSelected } = useUploadCustomPagePdf({
        onSuccess: (param)=>{
            let { pdfName, pdfFileName } = param;
            onAddPage(createBlankPdfPage(generateUniqueId('custom-page'), nextDisplayOrder, pdfName, pdfFileName));
        }
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const isOpen = !!anchorEl;
    const hasTemplates = templates.length > 0;
    const handleClose = useCallback(()=>{
        setAnchorEl(null);
    }, []);
    const { openFilePicker: openPdfFilePicker, fileInputElement: pdfFileInputElement } = useFilePicker({
        accept: '.pdf',
        onFileSelected: handlePdfFileSelected
    });
    const handleCreateBlank = useCallback(()=>{
        handleClose();
        showSelectTypeModal(async (type)=>{
            const id = generateUniqueId('custom-page');
            if (type === 'video') {
                onAddPage(createBlankVideoPage(id, nextDisplayOrder));
            } else if (type === 'text') {
                onAddPage(createBlankTextPage(id, nextDisplayOrder, ''));
            } else if (type === 'pdf') {
                openPdfFilePicker();
            }
        });
    }, [
        handleClose,
        showSelectTypeModal,
        onAddPage,
        nextDisplayOrder,
        openPdfFilePicker
    ]);
    const handleSelectTemplate = useCallback(async (template)=>{
        handleClose();
        const id = generateUniqueId('custom-page');
        const displayOrder = nextDisplayOrder;
        const baseFields = {
            id,
            pageTitle: template.pageTitle,
            displayOrder,
            displayScope: template.displayScope
        };
        switch(template.type){
            case 'video':
                onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                    type: 'video',
                    video: {
                        videoUrl: template.video.videoUrl
                    }
                }));
                break;
            case 'pdf':
                onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                    type: 'pdf',
                    pdf: {
                        pdfName: template.pdf.pdfName,
                        pdfFileName: template.pdf.pdfFileName
                    }
                }));
                break;
            case 'text':
                {
                    try {
                        const { textId } = await cloneText({
                            sourceTextId: template.text.textId
                        });
                        onAddPage(_object_spread_props(_object_spread({}, baseFields), {
                            type: 'text',
                            text: {
                                textId
                            }
                        }));
                    } catch (unused) {
                        showErrorToast(t.customPages.templates.loadContentError());
                    }
                    break;
                }
        }
    }, [
        handleClose,
        nextDisplayOrder,
        onAddPage,
        cloneText,
        showErrorToast,
        t
    ]);
    const handleManageTemplates = useCallback(()=>{
        handleClose();
        showModal({
            biName: 'custom_page_templates_modal',
            openBottomSheetOnMobile: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(CustomPageTemplatesModal, {
                    dismissModal: dismissModal
                });
            }
        });
    }, [
        handleClose,
        showModal
    ]);
    const handleAddPageClick = useCallback((event)=>{
        if (hasTemplates || isLoading) {
            setAnchorEl(event.currentTarget);
        } else {
            handleCreateBlank();
        }
    }, [
        hasTemplates,
        isLoading,
        handleCreateBlank
    ]);
    const menuItems = useMemo(()=>templates.map((tmpl)=>({
                id: tmpl.id,
                title: tmpl.name,
                body: tmpl.pageTitle,
                bi: {
                    name: 'select_custom_page_template',
                    data: {
                        template_id: tmpl.id
                    }
                },
                onClick: ()=>handleSelectTemplate(tmpl)
            })), [
        templates,
        handleSelectTemplate
    ]);
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.AddCustomPageDropdownTID.Root(),
        children: [
            pdfFileInputElement,
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                className: sectionStyles.addPageSlot,
                "data-testid": testIds.AddCustomPageDropdownTID.AddButton(),
                onClick: handleAddPageClick,
                children: [
                    /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 16
                    }),
                    t.customPages.addCustomPage()
                ]
            }),
            (hasTemplates || isLoading) && /*#__PURE__*/ _jsx(Popover, {
                anchorEl: anchorEl,
                open: isOpen,
                anchorOrigin: {
                    vertical: -8,
                    horizontal: 'center'
                },
                transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center'
                },
                onClose: handleClose,
                children: /*#__PURE__*/ _jsx(Menu, {
                    testId: testIds.AddCustomPageDropdownTID.Menu(),
                    dimensions: {
                        height: 400
                    },
                    loading: isLoading,
                    emptyText: t.customPages.dropdownNoMatches(),
                    searchProps: {
                        showSearch: true,
                        autoFocus: true,
                        bi: {
                            name: 'custom_page_template_search'
                        },
                        strategy: {
                            type: 'text'
                        }
                    },
                    items: menuItems,
                    footer: {
                        items: [
                            {
                                variant: 'contextualAction',
                                props: {
                                    title: t.customPages.addNew(),
                                    icon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                                    testId: testIds.AddCustomPageDropdownTID.CreateBlank(),
                                    onClick: handleCreateBlank,
                                    bi: {
                                        name: 'create_blank_custom_page'
                                    }
                                }
                            },
                            {
                                variant: 'contextualAction',
                                props: {
                                    title: t.customPages.manageTemplates(),
                                    icon: /*#__PURE__*/ _jsx(ArrowRight, {}),
                                    testId: testIds.AddCustomPageDropdownTID.ManageTemplates(),
                                    onClick: handleManageTemplates,
                                    bi: {
                                        name: 'manage_custom_page_templates'
                                    }
                                }
                            }
                        ]
                    }
                })
            })
        ]
    });
};
